<template>
  <draggable
    class="form-fields row"
    v-model="fields"
    group="formFields"
    handle=".handle"
    @change="reorder"
    @start="drag=true"
    @end="drag=false">
    <div
      class="form-field"
      :class="{
        'col-sm mb-3': !field.type.includes('layout'),
        'w-100': field.type.includes('layout'),
        'px-3': field.type === 'layout-linebreak',
      }"
      v-for="field in filteredFields"
      :key="field.id">

      <!-- Fields / Static Elements -->
      <div v-if="!field.type.includes('layout')">
        <!-- Label -->
        <label
          v-if="field.type !== 'checkbox' && !field.type.includes('static')"
          :for="`form-field-${field.id}`">
          <div>
            {{field.label}}
            <small v-if="field.required && !isHideRequired" class="text-danger">required</small>
          </div>
        </label>
        <!-- / Label -->

        <!-- Text -->
        <b-form-input
          :id="`form-field-${field.id}`"
          v-if="field.type === 'text'"
          :readonly="isReadonly"
          v-model="formData[field.name]"
          type="text">
        </b-form-input>
        <!-- / Text -->

        <!-- Number -->
        <b-form-input
          :id="`form-field-${field.id}`"
          v-if="field.type === 'number'"
          :readonly="isReadonly"
          v-model="formData[field.name]"
          type="number">
        </b-form-input>
        <!-- / Number -->

        <!-- Dropdown -->
        <b-form-select
          :id="`form-field-${field.id}`"
          :readonly="isReadonly"
          v-model="formData[field.name]"
          v-if="field.type === 'dropdown'">
          <option selected :value="undefined">Select Option</option>
          <option
            v-for="o in field.options"
            :key="o"
            :value="o">
            {{o}}
          </option>
        </b-form-select>
        <!-- / Dropdown -->

        <!-- Textarea -->
        <b-form-textarea
          :id="`form-field-${field.id}`"
          :readonly="isReadonly"
          v-model="formData[field.name]"
          rows="3"
          v-if="field.type === 'textarea'"
        />
        <!-- / Textarea -->

        <!-- Checkbox -->
        <b-form-checkbox
          v-if="field.type === 'checkbox'"
          :readonly="isReadonly"
          v-model="formData[field.name]">
          {{field.label}}
          <span v-if="field.required" class="text-danger">(required)</span>
        </b-form-checkbox>
        <!-- / Checkbox -->
      </div>
      <!-- / Fields / Static Elements -->

      <!-- New Line  -->
      <div
        style="font-size: 14px;"
        v-if="field.type === 'layout-linebreak' && !isHideButtons">
        New Line
      </div>
      <!-- / New Line -->

      <!-- Horizontal Rule  -->
      <hr
        :class="{'mb-0':!isHideButtons}"
        v-if="field.type === 'layout-hr'">
      <!-- / Horizontal Rule -->

      <!-- Paragraph -->
      <p v-if="field.type === 'static-paragraph'">
        {{field.label}}
      </p>
      <!-- / Paragraph -->

      <!-- Header -->
      <h2 v-if="field.type === 'static-header'">
        {{field.label}}
      </h2>
      <!-- / Header -->

      <!-- Header -->
      <a
        v-if="field.type === 'static-link'"
        :href="field.meta.url"
        target="_blank">
        {{field.label}}
      </a>
      <!-- / Header -->

      <!-- Edit / Delete Field -->
      <div
        v-if="!isHideButtons"
        class="d-flex ml-n2"
        :class="{
          'mb-2': field.type.includes('layout'),
          'px-3': field.type === 'layout-hr',
        }">
        <b-button
          type="button"
          size="sm"
          variant="link"
          v-if="!field.type.includes('layout')"
          @click="$emit('edit-field', field)">
          Edit
        </b-button>

        <b-button
          class="handle"
          type="button"
          variant="link"
          size="sm"
          v-b-tooltip="{
            title: 'Drag to reorder element.',
            placement: 'bottom',
            delay: { show: 1000, hide: 0 },
          }">
          Move
        </b-button>

        <!-- Price Modifiers -->
        <div v-if="!field.type.includes('layout')
          && !field.type.includes('static')">
          <b-button
            type="button"
            variant="link"
            size="sm"
            v-if="getModifiersForField(field.id).length === 0"
            @click="$emit('add-price-modifier', {
              form_field: field,
              form_field_id: field.id,
            })">
            Add Price Modifier
          </b-button>

          <b-button
            type="button"
            variant="link"
            v-else
            size="sm"
            @click="$emit('show-price-modifiers', field)">
            {{getModifiersForField(field.id).length}}
            Price Modifier{{getModifiersForField(field.id).length > 1 ? 's' : ''}}
          </b-button>
        </div>
        <!-- / Price Modifiers -->

        <b-button
          type="button"
          variant="link"
          size="sm"
          @click="$emit('remove-field', field)">
          Delete
        </b-button>
      </div>
      <!-- / Edit / Delete Field -->

      <!-- Hidden Field -->
      <b-badge
        v-if="field.hidden"
        variant="secondary">
        Hidden
      </b-badge>
      <!-- / Hidden Field -->
    </div>
  </draggable>
</template>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';

export default {
  name: 'form-fields',
  components: {
    draggable,
  },
  props: [
    'value',
    'formFields',
    'hideButtons',
    'hideRequired',
    'readonly',
    'fieldFilter',
    'priceModifiers',
  ],
  data() {
    return {
      formData: _.cloneDeep(this.value) || {},
      isReadonly: this.readonly === '',
      fields: _.cloneDeep(this.formFields),
      drag: false,
    };
  },
  watch: {
    formData: {
      deep: true,
      handler(data) {
        this.$emit('input', data);
      },
    },
    formFields() {
      this.fields = _.cloneDeep(this.formFields);
    },
  },
  computed: {
    isHideButtons() {
      return this.hideButtons === '' || this.hideButtons === true;
    },
    isHideRequired() {
      return this.hideRequired === '' || this.hideRequired === true;
    },
    filteredFields() {
      if (!this.fieldFilter) {
        return this.fields;
      }

      return _.filter(this.fields, (field) => {
        if (this.fieldFilter === 'Waiver') {
          return field.waiver === true;
        }

        return field.waiver === false;
      });
    },
  },
  methods: {
    reorder() {
      this.$emit('reorder-fields', this.fields);
    },
    getModifiersForField(fieldID) {
      return _.filter(this.priceModifiers, { form_field_id: fieldID });
    },
  },
};
</script>

<style lang="scss">
.form-fields {
  [readonly] {
    background-color: #ffffff !important;
    pointer-events: none;
  }

  .handle {
    cursor: move !important;
  }
}
</style>
